import type { ServiceInput, ServicePayload } from '@seek/indie-api-types';
import type { FilterData } from './types.ts';
import { mapSeriesLabelToFilterLabel, type GroupBy } from './config.ts';

import { useApi } from '#src/api/index.ts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { colorise } from './colorise.ts';
import { useLazyColorMode } from '#src/components/ColorModeProvider/ColorModeProvider.tsx';

export const useIntegrationHealthGraphData = ({
  bounds,
  filters,
  skipLoading,
  groupBy,
}: {
  bounds: [Date, Date];
  filters: FilterData[];
  skipLoading?: boolean;
  groupBy: GroupBy;
}) => {
  const api = useApi();

  const lazyColorMode = useLazyColorMode();

  const [boundsStart, boundsEnd] = bounds;
  const query = useQuery<{
    parameters: ServiceInput.IntegrationStatistics;
    results: ServicePayload.IntegrationStatistics;
  } | null>({
    queryKey: [
      'statistics',
      boundsStart.toISOString(),
      boundsEnd.toISOString(),
      ...filters.map((f) => [f.id]),
      skipLoading,
      groupBy,
      api,
    ],
    queryFn: async ({ signal }) => {
      if (skipLoading) {
        return null;
      }

      const parameters: ServiceInput.IntegrationStatistics = {
        timeRange: {
          start: boundsStart.toISOString(),
          end: boundsEnd.toISOString(),
        },
        // @ts-expect-error - annoying types
        filters: filters.map((f) => ({
          type: f.type,
          comparator: f.comparator,
          value: f.value,
        })),
        groupBy,
      };

      return {
        parameters,
        results: await api.integrationStatistics.get(parameters, signal),
      };
    },
    enabled: !skipLoading,
    placeholderData: keepPreviousData,
  });

  const data = useMemo(() => {
    if (query.status === 'success' && query.data !== null) {
      const queryData = query.data;

      const times = Array.from(
        new Set(
          queryData.results.series.flatMap((s) => s.values.map((v) => v.time)),
        ),
      ).sort();

      return {
        labels: times,
        metrics: queryData.results.metrics,
        datasets: colorise(
          lazyColorMode?.colorMode ?? 'lightMode',
          queryData.results.series.map((s) => ({
            label: mapSeriesLabelToFilterLabel(
              s.seriesLabel,
              queryData.parameters.groupBy,
            ),
            metrics: s.metrics,
            data: s.values.map((v) => ({
              x: v.time,
              y: v.value === 0 ? null : v.value,
            })),
          })),
        ),
      };
    }
  }, [lazyColorMode, query.status, query.data]);

  return { query, data };
};
